import { useCallback, useEffect, useMemo, useState } from "react";
import { isOnItsWay, type InventoryItem, type SupplierOrder } from "@dentacore/shared-types";

import {
  cancelSupplierBatch,
  cancelSupplierOrder,
  listSupplierOrders,
  markOrdersSent,
  receiveSupplierOrder,
} from "../api/suppliers";
import { formatMoney } from "../patients/money";
import { counted, DAY_GAP, DayCard, DayList, groupByDay } from "../patients/by-day";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { Pager, fitArea, useFitPages } from "../ui/paging";
import o from "./orders.module.css";
import { arrivedMessage, ReceiveDialog } from "./receive-dialog";
import { batchMessage, expectedText, shortDate } from "./supplier-text";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";
import { Badges, across, useShown } from "../shell/locations";
import type { Located } from "../shell/locations";

type Writer = (
  write: () => Promise<unknown>,
  reload: () => Promise<void>,
  done: string | ((result: unknown) => string),
) => Promise<boolean>;

/**
 * What a line costs: what came, at the invoice price, once it is closed; what was
 * ordered while any of it is still on its way (IA-D3).
 */
function lineCents(line: SupplierOrder): number {
  return line.status === "RECEIVED" || line.status === "CANCELLED"
    ? (line.receivedPriceCents ?? line.priceCents) * (line.receivedPacks ?? line.packs)
    : line.priceCents * line.packs;
}

/**
 * Inventory → *Orders* — STORY-171. What is on its way to this location, and
 * what came or was cancelled. *It arrived* and *Cancel* are for whoever records
 * stock movements (SU-D8).
 */
export function OrdersTab({
  items,
  currency,
  canOrder,
  canManagePrices,
  busy,
  run,
  fail,
  onStockChanged,
  resetOn,
}: {
  items: readonly InventoryItem[];
  currency: string;
  canOrder: boolean;
  /** May save an invoice price as the supplier's (IA-D4). */
  canManagePrices: boolean;
  busy: boolean;
  run: Writer;
  fail: (message: string) => void;
  /** An arrival moved a count, so the stock list must re-read. */
  onStockChanged: () => Promise<void>;
  resetOn: unknown;
}): React.JSX.Element {
  const [orders, setOrders] = useState<readonly SupplierOrder[] | null>(null);
  const [receiving, setReceiving] = useState<SupplierOrder | null>(null);
  const [cancelling, setCancelling] = useState<SupplierOrder | null>(null);
  const [cancellingBatch, setCancellingBatch] = useState<SupplierOrder | null>(null);

  // Every location shown: their orders together, each tagged.
  const places = useShown();
  const read = useCallback(
    (signal?: AbortSignal): Promise<readonly SupplierOrder[]> =>
      places.many
        ? across(places.ids, () => listSupplierOrders(signal))
        : listSupplierOrders(signal),
    [places.key],
  );

  const reload = useCallback(async () => {
    setOrders(await read());
  }, [read]);

  useEffect(() => {
    const controller = new AbortController();
    read(controller.signal)
      .then(setOrders)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setOrders([]);
        fail(cause instanceof Error ? cause.message : "Orders could not be loaded.");
      });
    return () => controller.abort();
  }, [fail, resetOn, read]);

  /**
   * **One row per order, not per item** (Emile, 2026-10-16: *"group by order
   * instead of showing individual items … it opens a side window just like other
   * pages"*). An order is what one checkout sent to one supplier — its lines
   * share a batch (MOD-D23); a line ordered on its own from a low-stock notice is
   * an order of one. The row says who, when, how many items, the total and where
   * it stands; the panel beside the list holds its lines and every action on
   * them. Orders with something still on the way come first, then newest.
   */
  const groups = useMemo(() => {
    const byKey = new Map<string, SupplierOrder[]>();
    for (const line of orders ?? []) {
      const key = line.batchId ?? line.id;
      byKey.set(key, [...(byKey.get(key) ?? []), line]);
    }
    return [...byKey.entries()]
      .map(([key, lines]) => {
        const first = lines[0] as SupplierOrder;
        // On its way includes a part-delivered line (IA-D3).
        const open = lines.filter((l) => isOnItsWay(l.status));
        const arrived = lines.filter((l) => l.status === "RECEIVED");
        // A cancelled order still shows what it was for.
        const kept = lines.filter((l) => l.status !== "CANCELLED");
        const total = (kept.length > 0 ? kept : lines).reduce((sum, l) => sum + lineCents(l), 0);
        // Placed but nobody has messaged the supplier yet (SU-D18).
        const unsent = open.filter((l) => l.sentAt === null);
        return { key, first, lines, open, arrived, unsent, total };
      })
      .sort((a, b) => b.first.orderedAt.localeCompare(a.first.orderedAt));
  }, [orders]);

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const shownGroup = groups.find((g) => g.key === openKey) ?? null;

  /** Where an order stands, as one short phrase. */
  const standing = (g: (typeof groups)[number]): { text: string; tone: string | undefined } => {
    // Not coming until somebody sends it, so it says so before anything else (SU-D19).
    if (g.unsent.length > 0) return { text: "Not sent yet", tone: styles.chipWarm };
    if (g.open.length === g.lines.length) {
      const latest = g.open
        .map((l) => expectedText(l.sentAt, l.leadDays))
        .filter((t): t is string => t !== null)
        .at(-1);
      return {
        text: latest === undefined ? "On the way" : `Expected ${latest}`,
        tone: styles.chipOrder,
      };
    }
    if (g.open.length > 0) {
      return {
        text: `${String(g.arrived.length)} of ${String(g.lines.length)} arrived`,
        tone: styles.chipOrder,
      };
    }
    if (g.arrived.length === 0) return { text: "Cancelled", tone: styles.chipPlain };
    const when = g.arrived
      .map((l) => l.receivedAt)
      .filter((t): t is string => t !== null)
      .sort()
      .at(-1);
    return { text: `Arrived ${when === undefined ? "" : shortDate(when)}`, tone: styles.chipPlain };
  };

  // By the day it was ordered, newest first, the way Billing groups invoices
  // (Emile, 2026-10-16: "group by day, in groups just like the design done for
  // billing"). What is still on the way says so on its own row.
  const days = groupByDay(groups, (g) => g.first.orderedAt);
  const pages = useFitPages({
    keys: days.map((day) => day.key),
    estimate: (index) => Math.max(76, 43 * (days[index]?.rows.length ?? 1)),
    gap: DAY_GAP,
    resetOn,
  });
  const [from, to] = pages.range;
  const onTheWay = groups.filter((g) => g.open.length > 0).length;

  const orderRow = (g: (typeof groups)[number]): React.JSX.Element => {
    const on = g.key === openKey;
    const state = standing(g);
    return (
      <button
        key={g.key}
        type="button"
        className={on ? `${o.cells} ${o.row} ${inv.rowOn}` : `${o.cells} ${o.row}`}
        aria-current={on ? "true" : undefined}
        onClick={() => setOpenKey(on ? null : g.key)}
        data-card=""
        data-testid={`order-row-${g.key}`}
      >
        <span className={o.who}>
          {g.first.supplierName}{" "}
          <Badges ats={[(g.first as Located<SupplierOrder>).at]} shown={places} />
        </span>
        <span className={inv.rowCount}>
          {g.lines.length} {g.lines.length === 1 ? "item" : "items"}
        </span>
        <span className={o.money}>{formatMoney(g.total, currency)}</span>
        <span className={o.state}>
          <span className={`${styles.chip} ${state.tone}`}>{state.text}</span>
        </span>
      </button>
    );
  };

  return (
    <div className={inv.body} data-testid="orders-tab">
      <section className={inv.listCard} data-testid="orders-list">
        <div className={inv.cardHead}>
          <span className={inv.headLabel}>Orders</span>
          <span className={inv.headCount}>
            {orders === null
              ? ""
              : `${String(onTheWay)} on the way · ${String(groups.length)} in all`}
          </span>
        </div>
        {orders === null || groups.length === 0 ? (
          <p className={styles.empty}>
            {orders === null
              ? "Loading…"
              : "Nothing ordered yet. Add items to the cart from the catalog, or order from a low-stock notice."}
          </p>
        ) : (
          <>
            <div className={`${fitArea} ${o.daysArea}`} ref={pages.areaRef}>
              <DayList testId="order-days">
                {days.slice(from, to).map((day) => (
                  <DayCard
                    key={day.key}
                    dayKey={day.key}
                    label={day.label}
                    count={counted(day.rows.length, "order")}
                    measureRef={pages.measure(day.key)}
                    flush
                  >
                    {day.rows.map(orderRow)}
                  </DayCard>
                ))}
              </DayList>
            </div>
            <div className={o.foot}>
              <Pager
                page={pages.page}
                count={pages.pages.length}
                onPage={pages.setPage}
                from={from + 1}
                to={to}
                total={days.length}
                noun="days"
                testId="orders-pager"
              />
            </div>
          </>
        )}
      </section>

      {shownGroup !== null && (
        <aside className={`${inv.panel} ${o.panel}`} data-testid="order-panel">
          <div className={inv.panelHead}>
            <h2 className={inv.panelTitle}>{shownGroup.first.supplierName}</h2>
            <button
              type="button"
              className={inv.close}
              onClick={() => setOpenKey(null)}
              aria-label="Close"
              data-testid="order-panel-close"
            >
              ×
            </button>
          </div>

          <div className={o.summary}>
            <span>
              Ordered {shortDate(shownGroup.first.orderedAt)}
              {shownGroup.first.orderedByName !== null && ` by ${shownGroup.first.orderedByName}`}
            </span>
            <span>
              {shownGroup.lines.length} {shownGroup.lines.length === 1 ? "item" : "items"} ·{" "}
              <b>{formatMoney(shownGroup.total, currency)}</b>
            </span>
            <span className={`${styles.chip} ${standing(shownGroup).tone}`}>
              {standing(shownGroup).text}
            </span>
          </div>

          {/*
            **The message, and saying it went** (SU-D18). It used to exist only in
            the checkout dialog, gone once that closed; it is here for as long as
            anything in the order is still to come. DentaCore sends nothing.
          */}
          {shownGroup.open.length > 0 && (
            <div className={o.sendBox} data-testid="order-send">
              <p className={o.sendText}>
                {shownGroup.unsent.length > 0
                  ? "Not sent yet. Copy the message, send it to the supplier, then mark it sent."
                  : `Sent ${shortDate(shownGroup.open[0]?.sentAt ?? shownGroup.first.orderedAt)}${
                      shownGroup.open[0]?.sentByName == null
                        ? ""
                        : ` by ${shownGroup.open[0].sentByName}`
                    }.`}
              </p>
              <div className={o.sendActions}>
                <button
                  type="button"
                  className={inv.ghost}
                  onClick={() => {
                    const message = batchMessage(
                      shownGroup.first.supplierName,
                      shownGroup.open.map((l) => ({
                        packs: l.packs,
                        itemName: l.itemName,
                        pack: l.pack,
                        priceCents: l.priceCents,
                      })),
                      shownGroup.open.reduce((sum, l) => sum + l.priceCents * l.packs, 0),
                      currency,
                      formatMoney,
                    );
                    void navigator.clipboard
                      .writeText(message)
                      .then(() => setCopied(shownGroup.key));
                  }}
                  data-testid="order-copy-message"
                >
                  {copied === shownGroup.key ? "Copied" : "Copy the message"}
                </button>
                {canOrder && shownGroup.unsent.length > 0 && (
                  <button
                    type="button"
                    className={inv.primary}
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () => markOrdersSent(shownGroup.unsent.map((l) => l.id)),
                        reload,
                        `Marked sent to ${shownGroup.first.supplierName}.`,
                      )
                    }
                    data-testid="order-mark-sent"
                  >
                    Mark as sent
                  </button>
                )}
              </div>
            </div>
          )}

          {canOrder && shownGroup.open.length > 1 && shownGroup.first.batchId !== null && (
            <div className={o.wholeOrder}>
              <button
                type="button"
                className={inv.ghost}
                disabled={busy}
                onClick={() => setCancellingBatch(shownGroup.open[0] ?? null)}
                data-testid={`order-cancel-all-${shownGroup.key}`}
              >
                Cancel what has not arrived
              </button>
            </div>
          )}

          <ul className={o.lines}>
            {shownGroup.lines.map((line) => {
              const expected = expectedText(line.sentAt, line.leadDays);
              const packs =
                line.status === "RECEIVED"
                  ? String(line.receivedPacks)
                  : line.status === "PARTIALLY_RECEIVED"
                    ? `${String(line.receivedPacks ?? 0)} of ${String(line.packs)}`
                    : String(line.packs);
              return (
                <li key={line.id} className={o.line} data-testid={`order-line-${line.id}`}>
                  <span className={o.lineMain}>
                    <span className={o.lineName}>{line.itemName}</span>
                    <span className={o.lineSub}>
                      {packs} × {line.pack}
                      {line.reason !== null && ` · not the cheapest: ${line.reason}`}
                    </span>
                  </span>
                  <span className={o.lineRight}>
                    <b>{formatMoney(lineCents(line), currency)}</b>
                    {line.status === "PARTIALLY_RECEIVED" ? (
                      <span className={`${styles.chip} ${styles.chipOrder}`}>
                        Part arrived · rest{" "}
                        {expected === null ? "on the way" : `expected ${expected}`}
                      </span>
                    ) : line.status === "OPEN" && line.sentAt === null ? (
                      <span className={`${styles.chip} ${styles.chipWarm}`}>Not sent yet</span>
                    ) : line.status === "OPEN" ? (
                      <span className={`${styles.chip} ${styles.chipOrder}`}>
                        {expected === null ? "On the way" : `Expected ${expected}`}
                      </span>
                    ) : line.status === "RECEIVED" ? (
                      <span className={`${styles.chip} ${styles.chipPlain}`}>
                        Arrived {line.receivedAt === null ? "" : shortDate(line.receivedAt)}
                      </span>
                    ) : (
                      <span className={`${styles.chip} ${styles.chipPlain}`}>Cancelled</span>
                    )}
                  </span>
                  {isOnItsWay(line.status) && canOrder && (
                    <span className={o.lineActions}>
                      <button
                        type="button"
                        className={inv.ghost}
                        disabled={busy}
                        onClick={() => setReceiving(line)}
                        data-testid={`order-receive-${line.id}`}
                      >
                        It arrived
                      </button>
                      <button
                        type="button"
                        className={inv.quiet}
                        disabled={busy}
                        onClick={() => setCancelling(line)}
                        data-testid={`order-cancel-${line.id}`}
                      >
                        Cancel
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>
      )}

      {receiving !== null && (
        <ReceiveDialog
          order={receiving}
          quantityOnHand={
            items.find((i) => i.id === receiving.inventoryItemId)?.quantityOnHand ?? null
          }
          currency={currency}
          busy={busy}
          canManagePrices={canManagePrices}
          onClose={() => setReceiving(null)}
          onReceive={(body) =>
            run(
              () => receiveSupplierOrder(receiving.id, body),
              async () => {
                await reload();
                await onStockChanged();
              },
              (result) => arrivedMessage(receiving, body, result as SupplierOrder),
            )
          }
        />
      )}

      {cancellingBatch?.batchId != null && (
        <ConfirmDialog
          title={`Cancel everything ordered from ${cancellingBatch.supplierName} together?`}
          confirmLabel="Cancel them all"
          cancelLabel="Keep them"
          tone="danger"
          busy={busy}
          onCancel={() => setCancellingBatch(null)}
          onConfirm={() => {
            const batchId = cancellingBatch.batchId;
            if (batchId === null) return;
            void run(
              () => cancelSupplierBatch(batchId),
              reload,
              "The lines still open were cancelled. Anything that already arrived stays.",
            ).then(() => setCancellingBatch(null));
          }}
        >
          Every line from this order that has not arrived yet is cancelled. This cannot be undone;
          you can order again.
        </ConfirmDialog>
      )}

      {cancelling !== null && (
        <ConfirmDialog
          title={`Cancel the ${cancelling.itemName} order?`}
          confirmLabel="Cancel the order"
          cancelLabel="Keep it"
          tone="danger"
          busy={busy}
          onCancel={() => setCancelling(null)}
          onConfirm={() => {
            const order = cancelling;
            void run(
              () => cancelSupplierOrder(order.id),
              reload,
              "Order cancelled. If the item is still low, its notice asks again.",
            ).then(() => setCancelling(null));
          }}
        >
          {cancelling.packs} × {cancelling.pack} from {cancelling.supplierName}. This cannot be
          undone; you can order again.
        </ConfirmDialog>
      )}
    </div>
  );
}
